import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  Platform,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import { supabase } from '../../api/supabase';
import { useAuthStore } from '../../stores/authStore';

// Types
interface Expense {
  id: string;
  date: string;
  category: string;
  amount: number;
  description: string;
  notes: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_by: string;
  approved_by: string | null;
  approved_at: string | null;
  receipt_url: string | null;
  created_at: string;
}

interface ExpenseStats {
  totalPending: number;
  totalApproved: number;
  totalRejected: number;
  countPending: number;
  countApproved: number;
  countRejected: number;
}

type ExpenseCategory =
  | 'maintenance'
  | 'fuel'
  | 'utilities'
  | 'salaries'
  | 'rent'
  | 'insurance'
  | 'office_supplies'
  | 'marketing'
  | 'other';

type ExpenseStatus = 'pending' | 'approved' | 'rejected' | 'all';

const CATEGORIES: { value: ExpenseCategory; label: string; icon: string }[] = [
  { value: 'maintenance', label: 'Maintenance', icon: 'construct' },
  { value: 'fuel', label: 'Fuel', icon: 'speedometer' },
  { value: 'utilities', label: 'Utilities', icon: 'flash' },
  { value: 'salaries', label: 'Salaries', icon: 'people' },
  { value: 'rent', label: 'Rent', icon: 'home' },
  { value: 'insurance', label: 'Insurance', icon: 'shield-checkmark' },
  { value: 'office_supplies', label: 'Office Supplies', icon: 'filing' },
  { value: 'marketing', label: 'Marketing', icon: 'megaphone' },
  { value: 'other', label: 'Other', icon: 'ellipsis-horizontal' },
];

const STATUS_COLORS = {
  pending: '#FF9500',
  approved: '#34C759',
  rejected: '#FF3B30',
};

const STATUS_ICONS = {
  pending: 'time-outline',
  approved: 'checkmark-circle',
  rejected: 'close-circle',
};

export default function CompanyExpenseTracker() {
  const { user } = useAuthStore();

  // Form state
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory>('other');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Data state
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [stats, setStats] = useState<ExpenseStats>({
    totalPending: 0,
    totalApproved: 0,
    totalRejected: 0,
    countPending: 0,
    countApproved: 0,
    countRejected: 0,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState<ExpenseStatus>('all');
  const [filterCategory, setFilterCategory] = useState<ExpenseCategory | 'all'>('all');

  useEffect(() => {
    loadExpenses();
  }, [filterStatus, filterCategory]);

  const loadExpenses = async () => {
    try {
      setLoading(true);

      // Build query
      let query = supabase.from('company_expenses').select('*').order('date', { ascending: false });

      // Apply status filter
      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      // Apply category filter
      if (filterCategory !== 'all') {
        query = query.eq('category', filterCategory);
      }

      const { data, error } = await query;

      if (error) throw error;

      setExpenses(data || []);
      calculateStats(data || []);
    } catch (error) {
      console.error('Error loading expenses:', error);
      Alert.alert('Error', 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (expenseList: Expense[]) => {
    const stats: ExpenseStats = {
      totalPending: 0,
      totalApproved: 0,
      totalRejected: 0,
      countPending: 0,
      countApproved: 0,
      countRejected: 0,
    };

    expenseList.forEach((expense) => {
      if (expense.status === 'pending') {
        stats.totalPending += expense.amount;
        stats.countPending++;
      } else if (expense.status === 'approved') {
        stats.totalApproved += expense.amount;
        stats.countApproved++;
      } else if (expense.status === 'rejected') {
        stats.totalRejected += expense.amount;
        stats.countRejected++;
      }
    });

    setStats(stats);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadExpenses();
    setRefreshing(false);
  };

  const handleDateChange = (event: any, date?: Date) => {
    setShowDatePicker(Platform.OS === 'ios');
    if (date) {
      setSelectedDate(date);
    }
  };

  const validateForm = (): boolean => {
    if (!amount || parseFloat(amount) <= 0) {
      Alert.alert('Validation Error', 'Please enter a valid amount');
      return false;
    }

    if (!description.trim()) {
      Alert.alert('Validation Error', 'Please enter a description');
      return false;
    }

    return true;
  };

  const handleSaveExpense = async () => {
    if (!validateForm()) return;
    if (!user) {
      Alert.alert('Error', 'User not authenticated');
      return;
    }

    try {
      setSaving(true);

      const expenseData = {
        date: selectedDate.toISOString().split('T')[0],
        category: selectedCategory,
        amount: parseFloat(amount),
        description: description.trim(),
        notes: notes.trim() || null,
        status: 'pending' as const,
        created_by: user.id,
      };

      const { error } = await supabase.from('company_expenses').insert([expenseData]);

      if (error) throw error;

      Alert.alert('Success', 'Expense recorded successfully');

      // Reset form
      setAmount('');
      setDescription('');
      setNotes('');
      setSelectedCategory('other');
      setSelectedDate(new Date());

      // Reload expenses
      await loadExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
      Alert.alert('Error', 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveExpense = async (expenseId: string) => {
    if (!user) {
      Alert.alert('Error', 'User not authenticated');
      return;
    }

    Alert.alert('Approve Expense', 'Are you sure you want to approve this expense?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Approve',
        onPress: async () => {
          try {
            const { error: expenseError } = await supabase
              .from('company_expenses')
              .update({
                status: 'approved',
                approved_by: user.id,
                approved_at: new Date().toISOString(),
              })
              .eq('id', expenseId);

            if (expenseError) throw expenseError;

            // Get expense details to create transaction
            const { data: expense } = await supabase
              .from('company_expenses')
              .select('*')
              .eq('id', expenseId)
              .single();

            if (expense) {
              // Create company cash transaction
              const { error: txError } = await supabase.from('company_cash_transactions').insert([
                {
                  date: expense.date,
                  category: 'expense',
                  subcategory: expense.category,
                  amount: -Math.abs(expense.amount), // Negative for expense
                  description: expense.description,
                  transaction_type: 'expense',
                  created_by: user.id,
                  is_verified: true,
                },
              ]);

              if (txError) throw txError;
            }

            Alert.alert('Success', 'Expense approved and transaction created');
            await loadExpenses();
          } catch (error) {
            console.error('Error approving expense:', error);
            Alert.alert('Error', 'Failed to approve expense');
          }
        },
      },
    ]);
  };

  const handleRejectExpense = async (expenseId: string) => {
    if (!user) {
      Alert.alert('Error', 'User not authenticated');
      return;
    }

    Alert.alert('Reject Expense', 'Are you sure you want to reject this expense?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Reject',
        style: 'destructive',
        onPress: async () => {
          try {
            const { error } = await supabase
              .from('company_expenses')
              .update({
                status: 'rejected',
                approved_by: user.id,
                approved_at: new Date().toISOString(),
              })
              .eq('id', expenseId);

            if (error) throw error;

            Alert.alert('Success', 'Expense rejected');
            await loadExpenses();
          } catch (error) {
            console.error('Error rejecting expense:', error);
            Alert.alert('Error', 'Failed to reject expense');
          }
        },
      },
    ]);
  };

  const getCategoryIcon = (category: string) => {
    const cat = CATEGORIES.find((c) => c.value === category);
    return cat?.icon || 'ellipsis-horizontal';
  };

  const getCategoryLabel = (category: string) => {
    const cat = CATEGORIES.find((c) => c.value === category);
    return cat?.label || 'Other';
  };

  const formatCurrency = (amount: number) => {
    return `AED ${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <ScrollView
      style={styles.container}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
    >
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Company Expenses</Text>
        <Text style={styles.subtitle}>Track and approve company spending</Text>
      </View>

      {/* Stats Cards */}
      <View style={styles.statsContainer}>
        <View style={[styles.statCard, { backgroundColor: '#FFF3E0' }]}>
          <Ionicons name="time-outline" size={24} color="#FF9500" />
          <Text style={styles.statValue}>{formatCurrency(stats.totalPending)}</Text>
          <Text style={styles.statLabel}>Pending ({stats.countPending})</Text>
        </View>

        <View style={[styles.statCard, { backgroundColor: '#E8F5E9' }]}>
          <Ionicons name="checkmark-circle" size={24} color="#34C759" />
          <Text style={styles.statValue}>{formatCurrency(stats.totalApproved)}</Text>
          <Text style={styles.statLabel}>Approved ({stats.countApproved})</Text>
        </View>

        <View style={[styles.statCard, { backgroundColor: '#FFEBEE' }]}>
          <Ionicons name="close-circle" size={24} color="#FF3B30" />
          <Text style={styles.statValue}>{formatCurrency(stats.totalRejected)}</Text>
          <Text style={styles.statLabel}>Rejected ({stats.countRejected})</Text>
        </View>
      </View>

      {/* Expense Entry Form */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Record New Expense</Text>

        {/* Date Selector */}
        <TouchableOpacity style={styles.inputContainer} onPress={() => setShowDatePicker(true)}>
          <Ionicons name="calendar-outline" size={20} color="#666" style={styles.inputIcon} />
          <Text style={styles.dateText}>{selectedDate.toLocaleDateString('en-US')}</Text>
        </TouchableOpacity>

        {showDatePicker && (
          <DateTimePicker
            value={selectedDate}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={handleDateChange}
            maximumDate={new Date()}
          />
        )}

        {/* Category Selector */}
        <View style={styles.categoryContainer}>
          <Text style={styles.label}>Category</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
            {CATEGORIES.map((cat) => (
              <TouchableOpacity
                key={cat.value}
                style={[styles.categoryChip, selectedCategory === cat.value && styles.categoryChipSelected]}
                onPress={() => setSelectedCategory(cat.value)}
              >
                <Ionicons
                  name={cat.icon as any}
                  size={18}
                  color={selectedCategory === cat.value ? '#FFFFFF' : '#666'}
                />
                <Text
                  style={[
                    styles.categoryChipText,
                    selectedCategory === cat.value && styles.categoryChipTextSelected,
                  ]}
                >
                  {cat.label}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Amount Input */}
        <View style={styles.inputContainer}>
          <Ionicons name="cash-outline" size={20} color="#666" style={styles.inputIcon} />
          <TextInput
            style={styles.input}
            placeholder="Amount (AED)"
            value={amount}
            onChangeText={setAmount}
            keyboardType="decimal-pad"
          />
        </View>

        {/* Description Input */}
        <View style={styles.inputContainer}>
          <Ionicons name="document-text-outline" size={20} color="#666" style={styles.inputIcon} />
          <TextInput
            style={styles.input}
            placeholder="Description (required)"
            value={description}
            onChangeText={setDescription}
          />
        </View>

        {/* Notes Input */}
        <View style={styles.inputContainer}>
          <Ionicons name="chatbox-outline" size={20} color="#666" style={styles.inputIcon} />
          <TextInput
            style={styles.input}
            placeholder="Notes (optional)"
            value={notes}
            onChangeText={setNotes}
            multiline
          />
        </View>

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveButton, saving && styles.saveButtonDisabled]}
          onPress={handleSaveExpense}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <>
              <Ionicons name="save-outline" size={20} color="#FFFFFF" />
              <Text style={styles.saveButtonText}>Save Expense</Text>
            </>
          )}
        </TouchableOpacity>
      </View>

      {/* Filters */}
      <View style={styles.filtersContainer}>
        <Text style={styles.filterLabel}>Status Filter:</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {(['all', 'pending', 'approved', 'rejected'] as ExpenseStatus[]).map((status) => (
            <TouchableOpacity
              key={status}
              style={[styles.filterChip, filterStatus === status && styles.filterChipActive]}
              onPress={() => setFilterStatus(status)}
            >
              <Text style={[styles.filterChipText, filterStatus === status && styles.filterChipTextActive]}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Expense List */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Expense History</Text>

        {loading ? (
          <ActivityIndicator size="large" color="#007AFF" style={styles.loader} />
        ) : expenses.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="document-outline" size={48} color="#CCC" />
            <Text style={styles.emptyStateText}>No expenses found</Text>
          </View>
        ) : (
          expenses.map((expense) => (
            <View key={expense.id} style={styles.expenseItem}>
              {/* Header */}
              <View style={styles.expenseHeader}>
                <View style={styles.expenseHeaderLeft}>
                  <Ionicons name={getCategoryIcon(expense.category) as any} size={24} color="#007AFF" />
                  <View style={styles.expenseInfo}>
                    <Text style={styles.expenseCategory}>{getCategoryLabel(expense.category)}</Text>
                    <Text style={styles.expenseDate}>{formatDate(expense.date)}</Text>
                  </View>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: STATUS_COLORS[expense.status] }]}>
                  <Ionicons name={STATUS_ICONS[expense.status] as any} size={14} color="#FFFFFF" />
                  <Text style={styles.statusBadgeText}>{expense.status.toUpperCase()}</Text>
                </View>
              </View>

              {/* Amount */}
              <Text style={styles.expenseAmount}>{formatCurrency(expense.amount)}</Text>

              {/* Description */}
              <Text style={styles.expenseDescription}>{expense.description}</Text>

              {/* Notes */}
              {expense.notes && <Text style={styles.expenseNotes}>Notes: {expense.notes}</Text>}

              {/* Actions (only for pending) */}
              {expense.status === 'pending' && (
                <View style={styles.expenseActions}>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.approveButton]}
                    onPress={() => handleApproveExpense(expense.id)}
                  >
                    <Ionicons name="checkmark-circle-outline" size={18} color="#FFFFFF" />
                    <Text style={styles.actionButtonText}>Approve</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.actionButton, styles.rejectButton]}
                    onPress={() => handleRejectExpense(expense.id)}
                  >
                    <Ionicons name="close-circle-outline" size={18} color="#FFFFFF" />
                    <Text style={styles.actionButtonText}>Reject</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          ))
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F7',
  },
  header: {
    padding: 20,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 15,
    color: '#666',
  },
  statsContainer: {
    flexDirection: 'row',
    padding: 15,
    gap: 10,
  },
  statCard: {
    flex: 1,
    padding: 15,
    borderRadius: 12,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000',
    marginTop: 8,
  },
  statLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  card: {
    backgroundColor: '#FFFFFF',
    margin: 15,
    marginTop: 0,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000',
    marginBottom: 15,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F5F7',
    borderRadius: 10,
    paddingHorizontal: 15,
    paddingVertical: 12,
    marginBottom: 12,
  },
  inputIcon: {
    marginRight: 10,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#000',
  },
  dateText: {
    fontSize: 16,
    color: '#000',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 10,
  },
  categoryContainer: {
    marginBottom: 15,
  },
  categoryScroll: {
    marginTop: 5,
  },
  categoryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F5F7',
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 10,
    gap: 6,
  },
  categoryChipSelected: {
    backgroundColor: '#007AFF',
  },
  categoryChipText: {
    fontSize: 14,
    color: '#666',
  },
  categoryChipTextSelected: {
    color: '#FFFFFF',
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#34C759',
    paddingVertical: 15,
    borderRadius: 10,
    marginTop: 10,
    gap: 8,
  },
  saveButtonDisabled: {
    backgroundColor: '#CCC',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  filtersContainer: {
    paddingHorizontal: 15,
    marginBottom: 15,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 10,
  },
  filterChip: {
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F5F5F7',
    marginRight: 10,
  },
  filterChipActive: {
    backgroundColor: '#007AFF',
  },
  filterChipText: {
    fontSize: 14,
    color: '#666',
  },
  filterChipTextActive: {
    color: '#FFFFFF',
  },
  loader: {
    marginVertical: 30,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#999',
    marginTop: 10,
  },
  expenseItem: {
    padding: 15,
    backgroundColor: '#F9F9F9',
    borderRadius: 10,
    marginBottom: 12,
  },
  expenseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  expenseHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  expenseInfo: {
    marginLeft: 12,
    flex: 1,
  },
  expenseCategory: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
  },
  expenseDate: {
    fontSize: 13,
    color: '#666',
    marginTop: 2,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    gap: 4,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  expenseAmount: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#FF3B30',
    marginBottom: 8,
  },
  expenseDescription: {
    fontSize: 15,
    color: '#000',
    marginBottom: 5,
  },
  expenseNotes: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
    marginTop: 5,
  },
  expenseActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 8,
    gap: 6,
  },
  approveButton: {
    backgroundColor: '#34C759',
  },
  rejectButton: {
    backgroundColor: '#FF3B30',
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
